/* UTILS */
import { computed, ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import { ASSESSMENT_RULES } from "@/components/af/RulesModal/assessmentRules"

/* STORES */
import { useHelpStore } from "@/components/af/Help/help.store"
import { useModalStore } from "@/components/af/Modal/Modal.store"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import { useQuestionsStore as useMoveQuestionsStore } from "@/features/move/store/questions.store"
import { useQuestionsStore as useMoveBisQuestionsStore } from "@/features/movebis/store/questions.store"

export type RulesModalTrigger = "tutorial" | "menu"

export const useRulesModalStore = defineStore("rulesModal", () => {
  const { trackModal } = useModalStore()

  const userStore = useUserStore()
  const { name: currentAssessment } = storeToRefs(useHelpStore())
  const { currentLang } = storeToRefs(useI18nStore())

  const { questionsLength: moveQuestionsLength } = storeToRefs(
    useMoveQuestionsStore()
  )
  const { questionsLength: moveBisQuestionsLength } = storeToRefs(
    useMoveBisQuestionsStore()
  )

  const isRulesModalOpen = ref(false)

  const assessmentName = ref("")

  const config = computed(() => ASSESSMENT_RULES[assessmentName.value])

  // Whether the rules can be reopened from the help menu
  const hasRules = computed(() => currentAssessment.value in ASSESSMENT_RULES)

  const statementValue = computed(() => {
    if (assessmentName.value === "Move") return moveQuestionsLength.value
    if (assessmentName.value === "MoveBis") return moveBisQuestionsLength.value
    return undefined
  })

  function open(assessment: string, trigger: RulesModalTrigger) {
    if (!(assessment in ASSESSMENT_RULES)) return

    assessmentName.value = assessment
    isRulesModalOpen.value = true

    trackModal({
      language: currentLang.value,
      name: "rules",
      assessment,
      trigger,
      user_email: userStore.getUserEmail,
      user_uuid: userStore.getUserUuid,
    })
  }

  function openForTutorial(assessment: string) {
    open(assessment, "tutorial")
  }

  // Reopen from the help menu
  function openRulesModal() {
    open(currentAssessment.value, "menu")
  }

  function closeRulesModal() {
    isRulesModalOpen.value = false
  }

  return {
    isRulesModalOpen,
    assessmentName,
    config,
    hasRules,
    statementValue,
    open,
    openForTutorial,
    openRulesModal,
    closeRulesModal,
  }
})
